import axios from "../../axiosInstance";
import requests from '../../requests';

import { takeLatest, all, put, call, delay } from "redux-saga/effects";

import { canvasActionTypes } from "./canvas.types";
import {
	fetchDiagramMembers,
	fetchDiagramMembersSuccess,
	fetchDiagramMembersFailure,

	changeRoleForDiagramMemberSuccess,
	changeRoleForDiagramMemberFailure,

	removeDiagramMemberSuccess,
	removeDiagramMemberFailure,

	fetchDiagramInvites,
	fetchDiagramInvitesSuccess,
	fetchDiagramInvitesFailure,

	createDiagramInviteSuccess,
	createDiagramInviteFailure,

	revokeDiagramInviteSuccess,
	revokeDiagramInviteFailure,
} from "./canvas.actions";

import { showNotify } from "../notify/notify.actions";

// Maps a diagram-invite error envelope `message` (POST /v1/co/diagrams/:id/invites)
// to a notify i18n key. The caller only reaches the invite UI as a diagram
// owner/admin, so a 403 `forbidden` here means they lack workspace-invite
// permission for a brand-new email.
const INVITE_ERROR_KEYS = {
	role_not_allowed_on_plan: 'role_not_allowed_on_plan',
	already_member: 'already_diagram_member',
	invite_already_pending: 'invite_already_pending',
	forbidden: 'cannot_invite_to_workspace',
};

function inviteErrorKey(e) {
	const message = e?.response?.data?.message;
	return INVITE_ERROR_KEYS[message] || 'something_wrong';
}

export function* fetchDiagramMembersRequest({ payload: { diagramId } }) {
	try {
		const url = `${requests.diagrams}/${diagramId}/members`;
		const { data, error } = yield axios.get(url, {});

		if (error) {
			yield put(fetchDiagramMembersFailure(error));
		} else {
			yield put(fetchDiagramMembersSuccess(data));
		}
	} catch (e) {
		yield put(fetchDiagramMembersFailure(e));
	}
}

export function* changeRoleForDiagramMemberRequest({ payload: { diagramId, memberId, newRole } }) {
	try {
		yield put(showNotify({ message: 'changing' }));

		const url = `${requests.diagrams}/${diagramId}/members/${memberId}/role`;
		const { data, error } = yield axios.post(url, { role: newRole });
		if (error) {
			yield put(changeRoleForDiagramMemberFailure(error));
			yield put(showNotify({ message: 'something_wrong' }));
		} else {
			yield put(changeRoleForDiagramMemberSuccess());
			yield put(showNotify({ message: 'role_changed' }));
			yield put(fetchDiagramMembers(diagramId));
		}
	} catch (e) {
		yield put(changeRoleForDiagramMemberFailure(e));
		yield put(showNotify({ message: 'something_wrong' }));
	}
}

export function* removeDiagramMemberRequest({ payload: { diagramId, memberId } }) {
	try {
		yield put(showNotify({ message: 'removing' }));

		const url = `${requests.diagrams}/${diagramId}/members/${memberId}`;
		const { data, error } = yield axios.delete(url, {});
		if (error) {
			yield put(removeDiagramMemberFailure(error));
			yield put(showNotify({ message: 'something_wrong' }));
		} else {
			yield put(removeDiagramMemberSuccess());
			yield put(showNotify({ message: 'member_removed' }));
			yield put(fetchDiagramMembers(diagramId));
		}
	} catch (e) {
		yield put(removeDiagramMemberFailure(e));
		yield put(showNotify({ message: 'something_wrong' }));
	}
}

export function* fetchDiagramInvitesRequest({ payload: { diagramId } }) {
	try {
		const url = `${requests.diagrams}/${diagramId}/invites`;
		const { data, error } = yield axios.get(url, {});

		if (error) {
			yield put(fetchDiagramInvitesFailure(error));
		} else {
			yield put(fetchDiagramInvitesSuccess(data));
		}
	} catch (e) {
		yield put(fetchDiagramInvitesFailure(e));
	}
}

export function* createDiagramInviteRequest({ payload: { diagramId, email, role, onSuccess, onError } }) {
	try {
		const url = `${requests.diagrams}/${diagramId}/invites`;
		const { data } = yield axios.post(url, { email, role });

		yield put(createDiagramInviteSuccess(data));

		if (data?.added) {
			// Email already belongs to a workspace member — they were added to
			// the diagram immediately, so refresh the member list.
			yield put(showNotify({ message: 'added_to_diagram' }));
			yield put(fetchDiagramMembers(diagramId));
		} else {
			// New email — a workspace invite was sent; diagram access is granted
			// when they accept it. Refresh the pending-invite list.
			yield put(showNotify({ message: 'invite_sent' }));
			yield put(fetchDiagramInvites(diagramId));
		}

		// Let the caller (the share modal) reflect the real outcome — only now
		// does it show "Invited ✓" and clear the input.
		if (typeof onSuccess === 'function') onSuccess(data);
	} catch (e) {
		yield put(createDiagramInviteFailure(e));
		yield put(showNotify({ message: inviteErrorKey(e) }));

		// Caller keeps the typed email and refocuses; the notify above is the
		// user-facing error.
		if (typeof onError === 'function') onError(e);
	}
}

export function* revokeDiagramInviteRequest({ payload: { diagramId, inviteId } }) {
	try {
		yield put(showNotify({ message: 'removing' }));

		const url = `${requests.diagrams}/${diagramId}/invites/${inviteId}`;
		yield axios.delete(url, {});

		yield put(revokeDiagramInviteSuccess());
		yield put(showNotify({ message: 'invite_removed' }));
		yield put(fetchDiagramInvites(diagramId));
	} catch (e) {
		yield put(revokeDiagramInviteFailure(e));
		yield put(showNotify({ message: 'something_wrong' }));
	}
}

export function* onFetchDiagramMembers() {
	yield takeLatest(canvasActionTypes.FETCH_DIAGRAM_MEMBERS_REQUEST, fetchDiagramMembersRequest);
}
export function* onChangeRoleForDiagramMember() {
	yield takeLatest(canvasActionTypes.CHANGE_MEMBER_ROLE_REQUEST, changeRoleForDiagramMemberRequest);
}
export function* onRemoveDiagramMember() {
	yield takeLatest(canvasActionTypes.REMOVE_DIAGRAM_MEMBER_REQUEST, removeDiagramMemberRequest);
}
export function* onFetchDiagramInvites() {
	yield takeLatest(canvasActionTypes.FETCH_DIAGRAM_INVITES_REQUEST, fetchDiagramInvitesRequest);
}
export function* onCreateDiagramInviteRequest() {
	yield takeLatest(canvasActionTypes.CREATE_DIAGRAM_INVITE_REQUEST, createDiagramInviteRequest);
}
export function* onRevokeDiagramInviteRequest() {
	yield takeLatest(canvasActionTypes.REVOKE_DIAGRAM_INVITE_REQUEST, revokeDiagramInviteRequest);
}

export function* canvasSagas() {
	yield all
		([
			call(onFetchDiagramMembers),
			call(onChangeRoleForDiagramMember),
			call(onRemoveDiagramMember),

			call(onFetchDiagramInvites),
			call(onCreateDiagramInviteRequest),
			call(onRevokeDiagramInviteRequest),
		]);
}